import { useEffect, useSyncExternalStore } from "react";
import { PRODUCT_TOP_TABS, isItemActive, matchProductGroup, pageTitle } from "../navigation";

/**
 * The pages this browser opened, for Home's shortcuts and the sidebar's
 * Recent lists: when each was last opened and how often. It lives in
 * local storage, so it is per browser, like the starred chats.
 */
export type PageVisit = {
  path: string;
  label: string;
  /** Shown in grey after the label: the page or section the path sits under. */
  context: string | null;
  count: number;
  lastAt: number;
};

/**
 * The sidebar page a path belongs to, as in the desktop app: a detail
 * page belongs to its list page, and the pages the sidebar does not list
 * belong to the one they sit beside.
 */
export type SidebarParent = "home" | "episodes" | "datasets" | "robots" | "policies" | "devservers";

const STORAGE_KEY = "bastion.pageVisits";
/** Enough for a month of normal use; the least recent drop first. */
const MAX_PAGES = 60;

const listeners = new Set<() => void>();
let cache: PageVisit[] | null = null;
/** Names a page gave itself (usePageName), kept until its visit is recorded. */
const names = new Map<string, { label: string; context: string | null }>();

function load(): PageVisit[] {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cache = raw ? (JSON.parse(raw) as PageVisit[]) : [];
  } catch {
    cache = [];
  }
  return cache;
}

function save(visits: PageVisit[]) {
  cache = visits;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
  } catch {
    // The list lasts for this visit.
  }
  for (const listener of listeners) listener();
}

/** The path of the nav page a path is under, the longest that matches. */
function navPath(pathname: string): string | null {
  let best: string | null = null;
  for (const tab of PRODUCT_TOP_TABS) {
    for (const item of tab.items) {
      if (item.path === "/" || !isItemActive(pathname, item.path)) continue;
      if (!best || item.path.length > best.length) best = item.path;
    }
  }
  return best;
}

/** True for a page of one thing (an episode, a robot), not a list page. */
export function isDetailPage(pathname: string): boolean {
  const base = navPath(pathname);
  if (base === null || pathname === base || !pathname.startsWith(`${base}/`)) return false;
  // A form to make or change a thing is not the thing.
  const last = pathname.split("/").filter(Boolean).pop();
  return last !== "new" && last !== "edit";
}

export function sidebarParent(pathname: string): SidebarParent | null {
  const group = matchProductGroup(pathname);
  if (group === "chat" || group === "analytics") return "home";
  if (group === "fleet") return "robots";
  if (group === "ml-studio") return pathname.startsWith("/devservers") ? "devservers" : "policies";
  if (group === "data-studio") {
    if (pathname.startsWith("/datasets")) return "datasets";
    return "episodes";
  }
  return null;
}

/** A detail page's own name: the last part of its path, shortened. */
function detailName(pathname: string): string | null {
  if (!isDetailPage(pathname)) return null;
  const last = decodeURIComponent(pathname.split("/").filter(Boolean).pop() ?? "");
  if (!last) return null;
  return last.length > 28 ? `${last.slice(0, 12)}…${last.slice(-8)}` : last;
}

function describe(pathname: string): { label: string; context: string | null } | null {
  // Home itself and chats are not shortcuts: the chats have their own list.
  if (matchProductGroup(pathname) === "chat") return null;
  const named = names.get(pathname);
  if (named) return named;
  const { page, section } = pageTitle(pathname);
  const detail = detailName(pathname);
  if (detail) return { label: detail, context: page };
  return { label: page, context: section };
}

/** Count a page open. AppLayout calls it on every route change. */
export function recordPageVisit(pathname: string, now = Date.now()) {
  const described = describe(pathname);
  if (!described || !described.label) return;
  const visits = load().filter((visit) => visit.path !== pathname);
  const previous = load().find((visit) => visit.path === pathname);
  visits.unshift({
    path: pathname,
    ...described,
    count: (previous?.count ?? 0) + 1,
    lastAt: now,
  });
  save(visits.slice(0, MAX_PAGES));
}

/**
 * Give the current page a readable name for Recent, Starred and Home, in
 * place of the last part of its URL: an episode's task, a robot's name.
 * A detail page calls it once its data has loaded.
 */
export function usePageName(pathname: string, label: string | null | undefined, context?: string | null) {
  useEffect(() => {
    if (!label) return;
    const name = { label, context: context ?? null };
    names.set(pathname, name);
    const visits = load();
    if (visits.some((visit) => visit.path === pathname)) {
      save(visits.map((visit) => (visit.path === pathname ? { ...visit, ...name } : visit)));
    }
  }, [pathname, label, context]);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The visited pages, most recent first. */
export function usePageVisits(): PageVisit[] {
  return useSyncExternalStore(subscribe, load, () => []);
}
